import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  DatePicker,
  Popconfirm,
  Select,
  Flex,
  Tooltip,
  message,
  Tag,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as actions from "store/actions/AuthActions";
import FilterBar, { FilterField } from "components/Common/FilterBar";
import ProductLabelsCell from "components/Common/ProductLabelsCell";
import PhoneNumberCell from "components/Common/PhoneNumberCell";
import { copyText } from "utils/clipboard";
import { ContactsTable } from "components/Contacts/RelatedContactsCard";
import LastActivityCell, { sortByLastActivity } from "components/Common/LastActivityCell";
import useUrlFilters from "hooks/useUrlFilters";

// Every filter, the search and the page live in the URL so a filtered list can
// be shared as a link.
const URL_FILTERS = {
  search: { type: "string" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 20 },
  products: { type: "array", key: "product" },
  accountType: { type: "string", key: "account_type" },
  customerType: { type: "string", key: "customer_type" },
  created: { type: "dateRange", keys: ["created_after", "created_before"] },
};

const AccountsTable = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter("search");
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  // Backend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter("page");
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter("pageSize");
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  // Products come from subscription billing, and are both the labels put on an
  // account and the values the Products filter offers.
  const [products, setProducts] = useState([]);
  const filterProducts = filters.products;
  const setFilterProducts = urlSetter("products");
  const filterAccountType = filters.accountType || undefined;
  const setFilterAccountType = urlSetter("accountType");
  const filterCustomerType = filters.customerType || undefined;
  const setFilterCustomerType = urlSetter("customerType");
  const filterCreated = filters.created;
  const setFilterCreated = urlSetter("created");

  // Contacts per account, fetched the first time a row is expanded:
  // { [accountId]: { loading, rows, error } }
  const [accountContacts, setAccountContacts] = useState({});

  // Choice lists are already loaded once by AccountsParentPage.
  const accountTypeOptions = useSelector((store) => store.accounts?.account_types?.choices) || [];
  const customerTypeOptions = useSelector((store) => store.accounts?.customer_types?.choices) || [];

  const fetchAccounts = async (page, size) => {
    setLoading(true);
    try {
      const response = await actions.loadAccounts(page, searchTerm, size, {
        products: filterProducts,
        account_type: filterAccountType,
        customer_type: filterCustomerType,
        created_after: filterCreated?.[0]?.format("YYYY-MM-DD"),
        created_before: filterCreated?.[1]?.format("YYYY-MM-DD"),
      });
      if (response.status === 200) {
        const data = response.data;
        setDataSource(data.results || []);
        setAccountContacts({});
        setTotalRows(data.count || 0);
      }
    } catch (e) {
      console.error("Error fetching accounts:", e);
    }
    setLoading(false);
  };

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page on search
    }
  };

  const fetchProducts = async () => {
    const response = await actions.loadProductPaymentProducts();
    if (response && response.data) {
      setProducts(Array.isArray(response.data) ? response.data : response.data.results || []);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch accounts when search term, product filter or pagination changes
  useEffect(() => {
    fetchAccounts(currentPage, pageSize);
  }, [searchTerm, currentPage, pageSize, filterProducts, filterAccountType, filterCustomerType, filterCreated]);

  const onFilterProductsChange = (value) => {
    setFilterProducts(value || []);
    setCurrentPage(1);
  };


  const clearFilters = () => {
    setFilterProducts([]);
    setFilterAccountType(undefined);
    setFilterCustomerType(undefined);
    setFilterCreated(null);
    setCurrentPage(1);
  };

  // Any filter change goes back to page 1 - staying on page 4 of a list that
  // just shrank to two pages shows an empty table.
  const applyFilter = (setter) => (value) => {
    setter(value);
    setCurrentPage(1);
  };

  const labelOf = (options, value) =>
    options.find((option) => option.value === value)?.label || value;

  const activeFilterCount =
    filterProducts.length + (filterAccountType ? 1 : 0)
    + (filterCustomerType ? 1 : 0) + (filterCreated ? 1 : 0);

  // One removable chip per applied value.
  const activeFilters = [
    ...filterProducts.map((productId) => ({
      key: `product-${productId}`,
      label: `Product: ${products.find((p) => p.id === productId)?.name || productId}`,
      onRemove: () =>
        onFilterProductsChange(filterProducts.filter((value) => value !== productId)),
    })),
    filterAccountType && {
      key: "account-type",
      label: `Account Type: ${labelOf(accountTypeOptions, filterAccountType)}`,
      onRemove: () => applyFilter(setFilterAccountType)(undefined),
    },
    filterCustomerType && {
      key: "customer-type",
      label: `Customer Type: ${labelOf(customerTypeOptions, filterCustomerType)}`,
      onRemove: () => applyFilter(setFilterCustomerType)(undefined),
    },
    filterCreated && {
      key: "created",
      label: `Created: ${filterCreated[0].format("D MMM YYYY")} - ${filterCreated[1].format("D MMM YYYY")}`,
      onRemove: () => applyFilter(setFilterCreated)(null),
    },
  ].filter(Boolean);

  const loadContactsFor = async (accountId) => {
    setAccountContacts((prev) => ({ ...prev, [accountId]: { loading: true, rows: prev[accountId]?.rows || [] } }));
    try {
      const rows = await actions.loadAccountContacts(accountId);
      setAccountContacts((prev) => ({ ...prev, [accountId]: { loading: false, rows } }));
    } catch (e) {
      console.error("Error fetching account contacts:", e);
      setAccountContacts((prev) => ({ ...prev, [accountId]: { loading: false, rows: [], error: true } }));
    }
  };

  const saveProducts = useCallback(async (accountId, productIds) => {
    const response = await actions.updateAccountProducts(accountId, productIds);
    // Keep the row in step with what was saved, so the labels survive a
    // re-render without refetching the whole page.
    setDataSource((rows) => rows.map((row) => (
      row.id === accountId ? { ...row, ...response.data } : row
    )));
    return response;
  }, []);

  // "Save as WhatsApp number" from the mobile column. The row is patched in
  // place so the WhatsApp cell next to it updates without a refetch.
  const saveWaNumber = useCallback(async (accountId, waNumber) => {
    const response = await actions.updateAccountWaNumber(accountId, waNumber);
    setDataSource((rows) => rows.map((row) => (
      row.id === accountId ? { ...row, ...response.data } : row
    )));
    return response;
  }, []);

  const handleDelete = useCallback(async (id) => {
    const newData = dataSource.find((item) => item.id === id);
    console.log(newData);
    const response = await actions.deleteAccounts(newData.id);
    console.log(response);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchAccounts(currentPage, pageSize);
    }
  }, [dataSource, currentPage, pageSize]);

  const copyAccountNumber = useCallback(async (accountNumber) => {
    const copied = await copyText(accountNumber);
    message[copied ? "success" : "error"](
      copied ? "Account number copied!" : "Could not copy the account number"
    );
  }, []);

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows
      );
    },
  };

  const defaultColumns = useMemo(() => [
    {
      title: "NUMBER",
      dataIndex: "account_number",
      key: "account_number",
      sorter: (a, b) =>
        (a?.account_number || "").localeCompare(b?.account_number || ""),
      width: 130,
      fixed: "left",
      render: (account_number, record) => (
        <a
          href={`/accounts/edit/${record.id}/`}
          onClick={(e) => e.stopPropagation()}
          style={{
            color: "#1890ff",
            textDecoration: "none",
            cursor: "pointer",
            fontWeight: 500,
          }}
          onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
          onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
        >
          {account_number}
        </a>
      ),
    },
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a?.name || "").localeCompare(b?.name || ""),
      width: 250,
      render: (text, record) => (
        <Tooltip title={text}>
          <a
            href={`/accounts/edit/${record.id}/`}
            onClick={(e) => e.stopPropagation()}
            style={{
              color: "#1890ff",
              textDecoration: "none",
              cursor: "pointer",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
          >
            {text}
          </a>
        </Tooltip>
      ),
    },
    {
      title: "CONTACTS",
      dataIndex: "contacts_count",
      key: "contacts_count",
      sorter: (a, b) => (a?.contacts_count || 0) - (b?.contacts_count || 0),
      width: 100,
      align: "center",
      render: (count) =>
        count ? (
          <Tag icon={<TeamOutlined />} color="blue">{count}</Tag>
        ) : (
          <span style={{ color: "#bfbfbf" }}>0</span>
        ),
    },
    {
      title: "TYPE",
      dataIndex: "customer_type",
      key: "customer_type",
      sorter: (a, b) => (a?.customer_type || "").localeCompare(b?.customer_type || ""),
      width: 70,
      align: "center",
      render: (text) => {
        const typeColors = {
          A: { bg: "#f6ffed", color: "#52c41a", label: "Amazing" },
          B: { bg: "#e6f7ff", color: "#1890ff", label: "Breadwinning" },
          C: { bg: "#fffbe6", color: "#faad14", label: "Convenience" },
          D: { bg: "#fff1f0", color: "#ff4d4f", label: "Dangerous" },
        };
        const style = typeColors[text] || { bg: "#fafafa", color: "#999", label: "Unknown" };
        return text ? (
          <Tooltip title={`${text} - ${style.label}`}>
            <span
              style={{
                backgroundColor: style.bg,
                color: style.color,
                padding: "2px 8px",
                borderRadius: "4px",
                fontWeight: "bold",
              }}
            >
              {text}
            </span>
          </Tooltip>
        ) : null;
      },
    },
    {
      title: "PRODUCTS",
      dataIndex: "products_detail",
      key: "products_detail",
      width: 260,
      render: (value, record) => (
        <ProductLabelsCell
          value={value}
          options={products}
          onSave={(productIds) => saveProducts(record.id, productIds)}
        />
      ),
    },
    {
      title: "CONTACT EMAIL",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => (a?.email || "").localeCompare(b?.email || ""),
      width: 220,
    },
    {
      title: "ADDRESS",
      dataIndex: "address",
      key: "address",
      width: 220,
    },
    {
      title: "GST NUMBER",
      dataIndex: "gst_number",
      key: "gst_number",
      sorter: (a, b) => (a?.gst_number || "").localeCompare(b?.gst_number || ""),
      width: 220,
    },
    {
      title: "MOBILE",
      dataIndex: "phone",
      key: "phone",
      sorter: (a, b) => (a?.phone || "").localeCompare(b?.phone || ""),
      width: 240,
      render: (phone, record) => (
        <PhoneNumberCell
          value={phone}
          waNumber={record.wa_number}
          onSaveWhatsapp={(number) => saveWaNumber(record.id, number)}
          recordLabel={record.name}
        />
      ),
    },
    {
      title: "WHATSAPP",
      dataIndex: "wa_number",
      key: "wa_number",
      sorter: (a, b) => (a?.wa_number || "").localeCompare(b?.wa_number || ""),
      width: 240,
      // Already the WhatsApp number, so only copying and opening the chat
      // make sense here.
      render: (wa_number) => <PhoneNumberCell value={wa_number} waNumber={wa_number} />,
    },
    {
      title: "WEBSITE",
      dataIndex: "website",
      key: "website",
      sorter: (a, b) => (a?.website || "").localeCompare(b?.website || ""),
      width: 220,
    },
    {
      title: "BALANCE",
      dataIndex: "balance",
      key: "balance",
      sorter: (a, b) => (a?.balance || 0) - (b?.balance || 0),
      width: 220,
    },
    {
      title: "PAID TO DATE",
      dataIndex: "paid_date",
      key: "paid_date",
      sorter: (a, b) => (a?.paid_date || "").localeCompare(b?.paid_date || ""),
      width: 220,
    },
    {
      title: "CREATED DATE",
      dataIndex: "create_date",
      key: "create_date",
      sorter: (a, b) => (a?.create_date || "").localeCompare(b?.create_date || ""),
      width: 220,
    },
    {
      title: "LAST ACTIVITY",
      dataIndex: "last_activity",
      key: "last_activity",
      sorter: sortByLastActivity,
      width: 160,
      render: (value) => <LastActivityCell value={value} />,
    },
    {
      title: "Actions",
      dataIndex: "edit",
      key: "edit",
      width: 150,
      render: (_, record) => (
        dataSource?.length >= 1 ? (
          <Flex gap="middle">
            <Tooltip title="Copy Number">
              <a onClick={() => copyAccountNumber(record.account_number)}>
                <CopyOutlined style={{ verticalAlign: "middle" }} />
              </a>
            </Tooltip>
            <Tooltip title="Edit">
              <a href={`/accounts/edit/${record.id}/`}>
                <EditOutlined style={{ verticalAlign: "middle" }} />
              </a>
            </Tooltip>
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => handleDelete(record.id)}
            >
              <a>
                <DeleteOutlined />
              </a>
            </Popconfirm>
          </Flex>
        ) : null
      ),
    },
  ], [dataSource?.length, handleDelete, copyAccountNumber, products, saveProducts, saveWaNumber]);

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      align: "center",
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState("checkbox");

  // Handle pagination change
  const handleTableChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    } else {
      setCurrentPage(page);
    }
  };


  return (
    <div style={{ padding: 10 }}>
      <div style={{ marginBottom: 8 }}>
        Showing {totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1}-
        {Math.min(currentPage * pageSize, totalRows)} of {totalRows}{" "}
        accounts
        </div>
      <Flex
        gap="large"
        justify="space-between"
        align="flex-start"
        wrap="wrap"
        style={{ width: "100%" }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search accounts"
            activeCount={activeFilterCount}
            activeFilters={activeFilters}
            onClear={clearFilters}
            title="Filter Accounts"
            onRefresh={() => fetchAccounts(currentPage, pageSize)}
            refreshing={loading}
            addLabel="Add Account"
            addIcon={<UserAddOutlined />}
            onAdd={() => navigate("/accounts/new")}
            addClassName="add-account-btn"
          >
            <FilterField label="Products">
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder="Filter by product"
                value={filterProducts}
                onChange={onFilterProductsChange}
                options={products.map((item) => ({ value: item.id, label: item.name }))}
                optionFilterProp="label"
              />
            </FilterField>
            <FilterField label="Account Type">
              <Select
                allowClear
                style={{ width: "100%" }}
                placeholder="Filter by account type"
                value={filterAccountType}
                onChange={applyFilter(setFilterAccountType)}
                options={accountTypeOptions}
              />
            </FilterField>
            <FilterField label="Customer Type">
              <Select
                allowClear
                style={{ width: "100%" }}
                placeholder="Filter by customer type"
                value={filterCustomerType}
                onChange={applyFilter(setFilterCustomerType)}
                options={customerTypeOptions}
              />
            </FilterField>
            <FilterField label="Created Between">
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                value={filterCreated}
                onChange={applyFilter(setFilterCreated)}
              />
            </FilterField>
          </FilterBar>
        </div>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          bordered
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={(row) => row.id}
          // Expand an account to see its contacts; loaded on first expand.
          expandable={{
            rowExpandable: (record) => (record.contacts_count || 0) > 0,
            onExpand: (expanded, record) => {
              if (expanded && !accountContacts[record.id]) {
                loadContactsFor(record.id);
              }
            },
            expandedRowRender: (record) => {
              const entry = accountContacts[record.id];
              return entry?.error ? (
                <span style={{ color: "#ff4d4f" }}>Failed to load contacts</span>
              ) : (
                <ContactsTable contacts={entry?.rows || []} loading={!entry || entry.loading} />
              );
            },
          }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ["20", "50", "100", "200"],
            onChange: handleTableChange,
          }}
          scroll={{
            x: "max-content", // Horizontal scroll
            y: "calc(100vh - 350px)", // Vertical scroll
          }}
          sticky
        />
      </div>
    </div>
  );
};
export default AccountsTable;
